import React from 'react';
import { Tab, Tabs } from '@mui/material';

export interface PageTab<T extends string> {
  value: T;
  label: string;
  /** A small icon before the label. */
  icon?: React.ReactElement;
}

export interface PageTabsProps<T extends string> {
  tabs: PageTab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the tabs for screen readers, e.g. "People". */
  ariaLabel: string;
}

/**
 * The sections of one page, as tabs along its top: Members | Roles | Shared
 * viewers. Each tab is a view of the same subject; keep the tab in the address
 * (`onChange` navigates) so a link opens the right one and Back works.
 *
 * For two to four choices of how to show the same data (Cases | Premium), use
 * `SegmentedControl`.
 */
export function PageTabs<T extends string>({ tabs, value, onChange, ariaLabel }: PageTabsProps<T>) {
  return (
    <Tabs
      value={value}
      onChange={(_e, next: T) => onChange(next)}
      aria-label={ariaLabel}
      variant="scrollable"
      scrollButtons={false}
      sx={(t) => ({
        minHeight: t.ds.sizes.control.lg,
        mb: 2.5,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        '& .MuiTab-root': {
          ...t.typography.subtitle2,
          minHeight: t.ds.sizes.control.lg,
          minWidth: 0,
          px: 1.5,
          textTransform: 'none',
          color: t.tokens.text.secondary,
          '&.Mui-selected': { color: t.tokens.text.primary },
        },
        '& .MuiTab-iconWrapper': { fontSize: t.typography.body1.fontSize },
        '& .MuiTabs-indicator': { bgcolor: t.tokens.brand.primary },
      })}
    >
      {tabs.map((tab) => (
        <Tab key={tab.value} value={tab.value} label={tab.label} icon={tab.icon} iconPosition="start" />
      ))}
    </Tabs>
  );
}
